import React, { useState } from 'react';
import {
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  FileCode2,
  Play,
  RotateCcw,
  Sparkles,
  Terminal,
  Activity,
  Cpu,
  Layers,
  FileCheck,
  Clock,
  Search,
  Plus,
  ExternalLink,
  ChevronRight,
  BarChart3,
  Server,
  Zap,
  Lock,
  Globe,
  Sliders,
  Award,
  Bug,
  BookOpen,
  ArrowRight,
  Scale,
  Code,
  Smartphone,
  Check
} from 'lucide-react';
import { AppView } from '../../types';
import { qualityStore, QualityMetricsSummary } from '../../services/quality/qualityStore';
import {
  FinancialModelDefinition,
  FinancialGoldenCase,
  FinancialPropertyDefinition,
  QualityDomain,
  ReleaseCandidate,
  ReleaseCertificate,
  QualityDefect,
  QualityDebtItem,
  QualityException
} from '../../services/quality/qualityTypes';

interface CQOProps {
  onNavigateToView: (view: AppView) => void;
}

export const ChiefQualityOfficerCommandCenter: React.FC<CQOProps> = ({ onNavigateToView }) => {
  const [metrics, setMetrics] = useState<QualityMetricsSummary>(() => qualityStore.getSummaryMetrics());
  const [activeTab, setActiveTab] = useState<
    | 'overview'
    | 'financial-models'
    | 'golden-cases'
    | 'property-testing'
    | 'automated-testing'
    | 'accessibility-devices'
    | 'ai-evaluation'
    | 'performance-synthetic'
    | 'resilience-chaos'
    | 'test-data'
    | 'defects-debt'
    | 'release-certification'
  >('overview');

  const [filterDomain, setFilterDomain] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [runningTests, setRunningTests] = useState<boolean>(false);
  const [testNotification, setTestNotification] = useState<string | null>(null);

  // Modals & New Entry States
  const [selectedGoldenCase, setSelectedGoldenCase] = useState<FinancialGoldenCase | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<FinancialPropertyDefinition | null>(null);
  const [selectedCertificate, setSelectedCertificate] = useState<ReleaseCertificate | null>(null);
  const [showNewDefectModal, setShowNewDefectModal] = useState<boolean>(false);
  const [showNewExceptionModal, setShowNewExceptionModal] = useState<boolean>(false);

  // New Defect Form
  const [defectForm, setDefectForm] = useState({
    title: '',
    domain: 'FINANCIAL_CORRECTNESS' as QualityDomain,
    severity: 'S1_HIGH' as 'S0_CRITICAL' | 'S1_HIGH' | 'S2_MEDIUM' | 'S3_LOW',
    environment: 'STAGING' as 'PRODUCTION' | 'STAGING' | 'CI',
    rootCause: '',
    owner: 'Elena Rostova'
  });

  // New Exception Form
  const [exceptionForm, setExceptionForm] = useState({
    gateDomain: 'PERFORMANCE' as QualityDomain,
    riskDescription: '',
    justification: '',
    mitigationPlan: '',
    owner: 'Harry Elie Chama',
    approver: 'Chief Quality Officer',
    expiresAt: '2026-09-15T00:00:00Z'
  });

  const handleRunAllGoldenCases = () => {
    setRunningTests(true);
    setTestNotification('Executing 44 Independent Golden Financial Cases against calculation engines...');
    setTimeout(() => {
      const res = qualityStore.runAllGoldenCases();
      setMetrics(qualityStore.getSummaryMetrics());
      setRunningTests(false);
      setTestNotification(`✓ All ${res.passed} Golden Financial Cases verified with 0.00 minor unit tolerance deviations.`);
      setTimeout(() => setTestNotification(null), 6000);
    }, 900);
  };

  const handleRunPropertyTests = () => {
    setRunningTests(true);
    setTestNotification('Running fast-check generative property suites (50,000 runs)...');
    setTimeout(() => {
      const res = qualityStore.runPropertyTests();
      setMetrics(qualityStore.getSummaryMetrics());
      setRunningTests(false);
      setTestNotification(`✓ Verified ${res.propertiesPassed} mathematical invariant properties across ${res.executed.toLocaleString()} generated test inputs.`);
      setTimeout(() => setTestNotification(null), 6000);
    }, 1100);
  };

  const handleCreateDefect = (e: React.FormEvent) => {
    e.preventDefault();
    if (!defectForm.title || !defectForm.rootCause) return;
    qualityStore.addDefect({
      title: defectForm.title,
      domain: defectForm.domain,
      severity: defectForm.severity,
      environment: defectForm.environment,
      rootCause: defectForm.rootCause,
      status: 'OPEN',
      regressionTestCreated: false,
      owner: defectForm.owner
    });
    setMetrics(qualityStore.getSummaryMetrics());
    setShowNewDefectModal(false);
    setDefectForm({
      title: '',
      domain: 'FINANCIAL_CORRECTNESS',
      severity: 'S1_HIGH',
      environment: 'STAGING',
      rootCause: '',
      owner: 'Elena Rostova'
    });
  };

  const handleCreateException = (e: React.FormEvent) => {
    e.preventDefault();
    if (!exceptionForm.riskDescription || !exceptionForm.justification) return;
    qualityStore.addException({
      gateDomain: exceptionForm.gateDomain,
      riskDescription: exceptionForm.riskDescription,
      justification: exceptionForm.justification,
      mitigationPlan: exceptionForm.mitigationPlan,
      owner: exceptionForm.owner,
      approver: exceptionForm.approver,
      expiresAt: exceptionForm.expiresAt
    });
    setMetrics(qualityStore.getSummaryMetrics());
    setShowNewExceptionModal(false);
  };

  const handleIssueCertificate = () => {
    const cert = qualityStore.issueReleaseCertificate('Harry Elie Chama (Chief Quality Officer)');
    setMetrics(qualityStore.getSummaryMetrics());
    setSelectedCertificate(cert);
    setTestNotification(`✓ Immutable Release Certificate ${cert.certificateId} issued for ${cert.releaseVersion}.`);
    setTimeout(() => setTestNotification(null), 6000);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A]">
      {/* Top Banner / CQO Navigation Bar */}
      <div className="bg-[#0F172A] text-white border-b border-[#1E293B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-600/20 border border-blue-500/40 flex items-center justify-center text-blue-400">
                <ShieldCheck className="w-7 h-7" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold tracking-tight">Chief Quality Officer Command Center</h1>
                  <span className="bg-blue-900/60 text-blue-300 text-xs px-2.5 py-0.5 rounded-full font-mono border border-blue-700/50">
                    CQO • Prompt 23
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-0.5">
                  Global Quality Engineering, Financial Model Validation, Automated Testing & Release Certification OS
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={handleRunAllGoldenCases}
                disabled={runningTests}
                className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <Play className="w-3.5 h-3.5" />
                <span>Run Golden Cases</span>
              </button>
              <button
                onClick={handleRunPropertyTests}
                disabled={runningTests}
                className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <Zap className="w-3.5 h-3.5" />
                <span>Run Property Tests</span>
              </button>
              <button
                onClick={() => onNavigateToView('qa-coherence-audit')}
                className="px-3.5 py-2 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold rounded-lg text-xs flex items-center gap-1.5 transition-colors cursor-pointer shadow-xs"
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                <span>Page 24 QA Audit</span>
              </button>
              <button
                onClick={() => onNavigateToView('admin-super-command-center')}
                className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-medium transition-colors cursor-pointer"
              >
                Super Admin
              </button>
              <button
                onClick={() => onNavigateToView('compliance-command-center')}
                className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-medium transition-colors cursor-pointer"
              >
                Compliance CCO
              </button>
            </div>
          </div>

          {testNotification && (
            <div className="mt-4 p-3 bg-blue-950/80 border border-blue-700 text-blue-200 text-xs rounded-xl flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-blue-400 animate-pulse" />
                <span className="font-mono">{testNotification}</span>
              </div>
              <button onClick={() => setTestNotification(null)} className="text-slate-400 hover:text-white text-xs">
                Dismiss
              </button>
            </div>
          )}

          {/* Quick Metrics Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mt-5 pt-4 border-t border-slate-800">
            <div className="bg-slate-900/60 p-2.5 rounded-lg border border-slate-800">
              <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Prod Release</div>
              <div className="text-xs font-bold text-white mt-0.5 truncate">{metrics.currentProductionRelease}</div>
            </div>
            <div className="bg-slate-900/60 p-2.5 rounded-lg border border-slate-800">
              <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Release Gate</div>
              <div className="text-xs font-bold text-emerald-400 mt-0.5 flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>{metrics.releaseCertificationStatus}</span>
              </div>
            </div>
            <div className="bg-slate-900/60 p-2.5 rounded-lg border border-slate-800">
              <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Golden Cases</div>
              <div className="text-xs font-bold text-emerald-400 mt-0.5">{metrics.goldenCasesPassRate.toFixed(1)}% Pass ({metrics.totalGoldenCases})</div>
            </div>
            <div className="bg-slate-900/60 p-2.5 rounded-lg border border-slate-800">
              <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Invariants</div>
              <div className="text-xs font-bold text-indigo-300 mt-0.5">{metrics.totalPropertiesGuaranteed} Proven (50k runs)</div>
            </div>
            <div className="bg-slate-900/60 p-2.5 rounded-lg border border-slate-800">
              <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Unit / Int Suite</div>
              <div className="text-xs font-bold text-slate-200 mt-0.5">{metrics.unitTestsCount} / {metrics.integrationTestsCount} (100%)</div>
            </div>
            <div className="bg-slate-900/60 p-2.5 rounded-lg border border-slate-800">
              <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">WCAG 2.2 AA</div>
              <div className="text-xs font-bold text-emerald-400 mt-0.5">0 Open Critical</div>
            </div>
            <div className="bg-slate-900/60 p-2.5 rounded-lg border border-slate-800">
              <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Synthetic SLA</div>
              <div className="text-xs font-bold text-emerald-400 mt-0.5">{metrics.syntheticAvailabilityPercent}% (4 Probes)</div>
            </div>
            <div className="bg-slate-900/60 p-2.5 rounded-lg border border-slate-800">
              <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Defects / Debt</div>
              <div className="text-xs font-bold text-amber-300 mt-0.5">{metrics.openDefectsCount} Open / {metrics.qualityDebtItemsCount} Debt</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Tab Navigation Bar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20 shadow-2xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-1 overflow-x-auto py-2.5 scrollbar-none">
            {[
              { id: 'overview', label: 'Executive Overview', icon: BarChart3 },
              { id: 'financial-models', label: 'Financial Models (P0)', icon: Scale },
              { id: 'golden-cases', label: 'Golden Cases & Provenance', icon: FileCheck },
              { id: 'property-testing', label: 'Property-Based Invariants', icon: Zap },
              { id: 'automated-testing', label: 'Unit / Int / Contracts / DB', icon: Code },
              { id: 'accessibility-devices', label: 'Accessibility & Devices', icon: Smartphone },
              { id: 'ai-evaluation', label: 'AI & Safety Evals', icon: Sparkles },
              { id: 'performance-synthetic', label: 'Performance & Synthetics', icon: Activity },
              { id: 'resilience-chaos', label: 'Chaos & Resilience', icon: ShieldCheck },
              { id: 'test-data', label: 'Test Data & Personas', icon: Layers },
              { id: 'defects-debt', label: 'Defects & Quality Debt', icon: Bug },
              { id: 'release-certification', label: 'Release Certification Gate', icon: Award },
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap flex items-center gap-1.5 transition-colors cursor-pointer ${
                    isActive
                      ? 'bg-blue-600 text-white shadow-xs'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* 1. OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Executive Summary Card */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-6 border-b border-slate-100">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-bold text-slate-900">Release Candidate v2026.8.4 Certification Status</h2>
                    <span className="bg-emerald-100 text-emerald-800 text-xs px-2.5 py-0.5 rounded-full font-bold">
                      FULLY CERTIFIED
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Multi-dimensional release assurance gate evaluated across 18 quality domains with zero critical regressions.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setActiveTab('release-certification')}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
                  >
                    <Award className="w-4 h-4" />
                    <span>View Release Certificate</span>
                  </button>
                </div>
              </div>

              {/* 18 Quality Domains Grid */}
              <div className="mt-6">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">
                  Multi-Dimensional Quality Gate Scorecard (18 Domains)
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {Object.entries(qualityStore.currentRelease.gateResults).map(([key, gate]) => (
                    <div
                      key={key}
                      className="p-3 rounded-xl border bg-slate-50/50 hover:bg-slate-50 transition-colors border-slate-200"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold text-slate-700 truncate">{gate.domain}</span>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                          gate.status === 'PASS' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                        }`}>
                          {gate.status}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-1.5 line-clamp-2 leading-relaxed">
                        {gate.summary}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Core Tenets & Anti-Patterns Reminder */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs">
                <div className="flex items-center gap-2 mb-4 text-emerald-700 font-bold text-sm">
                  <CheckCircle2 className="w-5 h-5" />
                  <span>YamooPay Quality Operating Principles</span>
                </div>
                <ul className="space-y-2.5 text-xs text-slate-600">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                    <span><strong>Financial Correctness ≠ Code Coverage:</strong> 100% line coverage means nothing if a tax bracket or rounding rule is wrong.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                    <span><strong>Independent Golden Provenance:</strong> An implementation cannot be its own oracle; test expected values must originate from regulators (IRS, HMRC, CRA, DGFiP, CFPB).</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                    <span><strong>Property-Based Generative Proof:</strong> Generative invariant checks (fast-check) guarantee monotonicity, conservation of principal, and boundary correctness.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                    <span><strong>Zero Blind Snapshot Updates:</strong> Golden cases are immutable; any expected change requires explicit CPA/auditor sign-off.</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs">
                <div className="flex items-center gap-2 mb-4 text-red-700 font-bold text-sm">
                  <AlertTriangle className="w-5 h-5" />
                  <span>Strict Quality Anti-Patterns Forbidden at YamooPay</span>
                </div>
                <ul className="space-y-2.5 text-xs text-slate-600">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 shrink-0" />
                    <span><strong>No Single Giant E2E Test Suite:</strong> We maintain a balanced test pyramid (Static → Unit → Financial → Contracts → E2E).</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 shrink-0" />
                    <span><strong>No Live Money or Real PII in CI:</strong> Test suites strictly utilize synthetic personas and sandbox provider keys.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 shrink-0" />
                    <span><strong>No Sleep-based Flake Fixes:</strong> Explicit condition polling and determinism required for all integration steps.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 shrink-0" />
                    <span><strong>No Opaque Single Quality Score:</strong> Release certification evaluates every dimension separately; critical gates cannot be averaged out.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* 2. FINANCIAL MODELS TAB (P0) */}
        {activeTab === 'financial-models' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Financial Model Governance Registry</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Official financial calculation models, active formula versions, and certified mathematical invariants.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleRunAllGoldenCases}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
                >
                  <Play className="w-4 h-4" />
                  <span>Revalidate All Models</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {qualityStore.models.map((model) => (
                <div
                  key={model.modelKey}
                  className="bg-white border border-slate-200 rounded-2xl p-5 shadow-2xs hover:border-blue-300 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded">
                          {model.jurisdiction}
                        </span>
                        <h3 className="text-sm font-bold text-slate-900">{model.name}</h3>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-3">
                        <span>Ruleset: <strong className="text-slate-700 font-mono">{model.ruleset}</strong></span>
                        <span>Version: <strong className="text-slate-700 font-mono">{model.formulaVersion}</strong></span>
                      </div>
                    </div>
                    <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full">
                      {model.status}
                    </span>
                  </div>

                  <p className="text-xs text-slate-600 mt-3 leading-relaxed">
                    {model.description}
                  </p>

                  <div className="mt-4 pt-3 border-t border-slate-100 space-y-2">
                    <div className="text-[11px] font-semibold text-slate-700">Guaranteed Invariants:</div>
                    <ul className="space-y-1">
                      {model.invariantsGuaranteed.map((inv, idx) => (
                        <li key={idx} className="text-[11px] text-slate-500 flex items-start gap-1.5">
                          <Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                          <span>{inv}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
                    <div>Validator: <strong className="text-slate-700">{model.validator}</strong></div>
                    <div className="flex items-center gap-1 text-emerald-600 font-bold">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span>{model.totalGoldenCases} Golden Cases (100%)</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 3. GOLDEN CASES TAB */}
        {activeTab === 'golden-cases' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Independent Financial Golden Cases (IRS, HMRC, CRA, CFPB)</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Pre-calculated reference test fixtures with documented statutory derivation methods and zero self-referencing.
                </p>
              </div>
              <button
                onClick={handleRunAllGoldenCases}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <Play className="w-4 h-4" />
                <span>Execute Golden Suite</span>
              </button>
            </div>

            <div className="space-y-3">
              {qualityStore.goldenCases.map((gc) => (
                <div
                  key={gc.caseId}
                  className="bg-white border border-slate-200 rounded-2xl p-5 shadow-2xs hover:border-blue-300 transition-colors"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <span className="font-mono text-xs font-bold text-slate-800 bg-slate-100 px-2 py-0.5 rounded">
                        {gc.caseId}
                      </span>
                      <h3 className="text-sm font-bold text-slate-900">{gc.title}</h3>
                      <span className="text-[10px] font-bold bg-blue-50 text-blue-800 px-2 py-0.5 rounded-full">
                        {gc.provenance}
                      </span>
                      {gc.isBoundaryCase && (
                        <span className="text-[10px] font-bold bg-purple-50 text-purple-800 px-2 py-0.5 rounded-full">
                          Boundary Test
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">Tolerance: <strong>±{gc.tolerance.toFixed(2)}</strong></span>
                      <span className="bg-emerald-100 text-emerald-800 text-xs px-2.5 py-0.5 rounded-full font-bold flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span>PASSED ({gc.lastRunResult?.durationMs}ms)</span>
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 bg-slate-50 p-3.5 rounded-xl border border-slate-100 text-xs">
                    <div>
                      <div className="font-bold text-slate-700 mb-1">Inputs:</div>
                      <pre className="font-mono text-[11px] text-slate-600 bg-white p-2 rounded border border-slate-200 overflow-x-auto">
                        {JSON.stringify(gc.inputs, null, 2)}
                      </pre>
                    </div>
                    <div>
                      <div className="font-bold text-slate-700 mb-1">Expected Outputs (Regulator Spec):</div>
                      <pre className="font-mono text-[11px] text-emerald-800 bg-white p-2 rounded border border-slate-200 overflow-x-auto">
                        {JSON.stringify(gc.expectedOutputs, null, 2)}
                      </pre>
                    </div>
                  </div>

                  <div className="mt-3 text-xs text-slate-600">
                    <strong>Source & Reference:</strong> {gc.sourceReference}
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    <strong>Derivation Method:</strong> {gc.derivationMethod}
                  </div>

                  <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
                    <span>Approved by: <strong className="text-slate-600">{gc.reviewer}</strong> on {gc.approvedAt.slice(0, 10)}</span>
                    <button
                      onClick={() => setSelectedGoldenCase(gc)}
                      className="text-blue-600 hover:text-blue-800 font-bold flex items-center gap-1 cursor-pointer"
                    >
                      <span>Inspect Audit Trail</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 4. PROPERTY-BASED TESTING TAB */}
        {activeTab === 'property-testing' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Generative Property-Based Testing (fast-check Invariants)</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Proving mathematical laws across millions of randomized and boundary-shrunk inputs.
                </p>
              </div>
              <button
                onClick={handleRunPropertyTests}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <Zap className="w-4 h-4" />
                <span>Run 50,000 Property Checks</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {qualityStore.properties.map((prop) => (
                <div
                  key={prop.propertyId}
                  className="bg-white border border-slate-200 rounded-2xl p-5 shadow-2xs hover:border-indigo-300 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">
                          {prop.type}
                        </span>
                        <h3 className="text-sm font-bold text-slate-900">{prop.name}</h3>
                      </div>
                      <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                        {prop.description}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 bg-slate-900 text-slate-200 p-3 rounded-xl text-xs font-mono">
                    <div className="text-[10px] text-slate-400 uppercase font-bold mb-1">Invariant Assertion:</div>
                    <div className="text-emerald-400 font-semibold">{prop.invariantStatement}</div>
                    <div className="text-[10px] text-slate-400 uppercase font-bold mt-2 mb-1">Generator Schema:</div>
                    <div className="text-indigo-300 text-[11px] truncate">{prop.generatorSchema}</div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                    <div className="text-slate-500">
                      Seed: <span className="font-mono font-bold text-slate-700">#{prop.lastSeed}</span>
                    </div>
                    <div className="flex items-center gap-1 text-emerald-600 font-bold">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span>{prop.runsPerCI} runs (0 failures)</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 5. AUTOMATED TESTING TAB */}
        {activeTab === 'automated-testing' && (
          <div className="space-y-8">
            {/* Contracts & Public API */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-bold text-slate-900">API Contract & Provider Adapter Tests</h3>
                  <p className="text-xs text-slate-500">OpenAPI 3.1 canonical contract verification and Webhook signature validation.</p>
                </div>
                <span className="bg-emerald-100 text-emerald-800 text-xs font-bold px-2.5 py-0.5 rounded-full">
                  100% COMPLIANT
                </span>
              </div>
              <div className="space-y-3">
                {qualityStore.contractTests.map((ct) => (
                  <div key={ct.contractId} className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs">
                    <div>
                      <div className="flex items-center gap-2 font-bold text-slate-900">
                        <span>{ct.name}</span>
                        <span className="font-mono text-[10px] bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">{ct.schemaVersion}</span>
                      </div>
                      <div className="text-slate-500 mt-1">{ct.details}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-emerald-700 font-bold flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span>{ct.status}</span>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Database Migrations */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-bold text-slate-900">Database Quality & Migration Lock Tests</h3>
                  <p className="text-xs text-slate-500">PostgreSQL migration idempotency, zero table lock &gt;50ms, and constraint verification.</p>
                </div>
                <span className="bg-emerald-100 text-emerald-800 text-xs font-bold px-2.5 py-0.5 rounded-full">
                  ZERO MIGRATION LOCKS
                </span>
              </div>
              <div className="space-y-3">
                {qualityStore.dbTests.map((dt) => (
                  <div key={dt.testId} className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs">
                    <div>
                      <div className="font-bold text-slate-900">{dt.name}</div>
                      <div className="text-slate-500 mt-1">{dt.notes}</div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 text-slate-600">
                      <span>Records: <strong>{dt.recordsTested.toLocaleString()}</strong></span>
                      <span>Lock: <strong className="text-emerald-700">{dt.lockDurationMs}ms</strong></span>
                      <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded font-bold">{dt.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Playwright E2E Matrix */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-bold text-slate-900">Playwright E2E Core Journeys (Chromium, Firefox, WebKit)</h3>
                  <p className="text-xs text-slate-500">Multi-browser automated user journeys with flake tracking.</p>
                </div>
                <span className="text-xs text-slate-500">Global Flake Rate: <strong className="text-emerald-700">0.15%</strong> (Budget: &lt;1.0%)</span>
              </div>
              <div className="space-y-3">
                {qualityStore.e2eJourneys.map((ej) => (
                  <div key={ej.journeyId} className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs">
                    <div>
                      <div className="font-bold text-slate-900">{ej.name}</div>
                      <div className="text-slate-500 mt-1 flex items-center gap-2">
                        <span>Role: <strong>{ej.userRole}</strong></span>
                        <span>•</span>
                        <span>Steps: <strong>{ej.stepsCount}</strong></span>
                        <span>•</span>
                        <span>Browsers: <strong className="text-slate-700">{ej.browsersTested.join(', ')}</strong></span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-slate-500">Duration: <strong>{(ej.durationMs / 1000).toFixed(1)}s</strong></span>
                      <span className="bg-emerald-100 text-emerald-800 px-2.5 py-0.5 rounded-full font-bold flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span>{ej.status}</span>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 6. ACCESSIBILITY & DEVICES TAB */}
        {activeTab === 'accessibility-devices' && (
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-bold text-slate-900">WCAG 2.2 Level AA & European Accessibility Act (EAA) Audits</h3>
                  <p className="text-xs text-slate-500">Automated axe-core scanning + manual keyboard focus traps and screen reader testing.</p>
                </div>
                <span className="bg-emerald-100 text-emerald-800 text-xs font-bold px-2.5 py-0.5 rounded-full">
                  0 CRITICAL VIOLATIONS
                </span>
              </div>

              <div className="space-y-3">
                {qualityStore.a11yFindings.map((finding) => (
                  <div key={finding.id} className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-xs">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-slate-800 bg-slate-200 px-2 py-0.5 rounded">{finding.wcagCriterion}</span>
                        <span className="font-bold text-slate-900">{finding.pageOrComponent}</span>
                      </div>
                      <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-bold">{finding.status}</span>
                    </div>
                    <p className="text-slate-600 mt-2">{finding.description}</p>
                    <div className="mt-2 text-slate-500">Impact: <strong>{finding.assistiveTechImpact}</strong></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 7. AI EVALUATION TAB */}
        {activeTab === 'ai-evaluation' && (
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-bold text-slate-900">AI Evaluation Platform & Safety Red-Teaming</h3>
                  <p className="text-xs text-slate-500">Evaluation against financial advice boundaries, tool-calling correctness, and prompt injection safety.</p>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span>Accuracy: <strong className="text-emerald-700">99.4%</strong></span>
                  <span>Safety Compliance: <strong className="text-emerald-700">100%</strong></span>
                </div>
              </div>

              <div className="space-y-3">
                {qualityStore.aiEvals.map((evalCase) => (
                  <div key={evalCase.caseId} className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-xs">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] font-bold bg-purple-100 text-purple-800 px-2 py-0.5 rounded">{evalCase.promptType}</span>
                        <span className="font-mono text-slate-500">{evalCase.caseId}</span>
                      </div>
                      <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-bold">{evalCase.status}</span>
                    </div>
                    <div className="mt-2 text-slate-900 font-semibold">Prompt: &quot;{evalCase.prompt}&quot;</div>
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 bg-white p-2.5 rounded-lg border border-slate-200">
                      <div>Correctness: <strong className="text-emerald-700">{evalCase.evalScores.correctness}%</strong></div>
                      <div>Groundedness: <strong className="text-emerald-700">{evalCase.evalScores.groundedness}%</strong></div>
                      <div>Tool Selection: <strong className="text-emerald-700">{evalCase.evalScores.toolSelection}%</strong></div>
                      <div>Latency: <strong>{evalCase.evalScores.latencyMs}ms</strong></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 8. PERFORMANCE & SYNTHETIC MONITORING TAB */}
        {activeTab === 'performance-synthetic' && (
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-bold text-slate-900">k6 Performance & Load Qualification Scenarios</h3>
                  <p className="text-xs text-slate-500">Latency budgets (P50, P95, P99) and autoscaling verification under traffic stress.</p>
                </div>
              </div>
              <div className="space-y-3">
                {qualityStore.perfScenarios.map((scenario) => (
                  <div key={scenario.scenarioId} className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-xs">
                    <div className="flex items-center justify-between">
                      <div className="font-bold text-slate-900">{scenario.name} ({scenario.type})</div>
                      <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-bold">{scenario.status}</span>
                    </div>
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2 bg-white p-2.5 rounded-lg border border-slate-200">
                      <div>Target VUs: <strong>{scenario.targetVUs.toLocaleString()}</strong></div>
                      <div>P50 Latency: <strong>{scenario.p50LatencyMs}ms</strong></div>
                      <div>P95 Latency: <strong className="text-emerald-700">{scenario.p95LatencyMs}ms</strong> (Limit: {scenario.thresholdP95Ms}ms)</div>
                      <div>P99 Latency: <strong>{scenario.p99LatencyMs}ms</strong></div>
                      <div>Error Rate: <strong>{scenario.errorRatePercent}%</strong></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-bold text-slate-900">Global Synthetic Monitoring Probes</h3>
                  <p className="text-xs text-slate-500">Non-financial synthetic health checks across North America, Europe, APAC, and Africa.</p>
                </div>
                <span className="bg-emerald-100 text-emerald-800 text-xs font-bold px-2.5 py-0.5 rounded-full">
                  ALL PROBES HEALTHY
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {qualityStore.syntheticProbes.map((probe) => (
                  <div key={probe.probeId} className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-900">{probe.name}</span>
                      <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full">{probe.status}</span>
                    </div>
                    <div className="mt-2 text-slate-500 font-mono text-[11px]">{probe.endpointOrJourney}</div>
                    <div className="mt-2 flex items-center justify-between text-slate-600">
                      <span>Availability: <strong className="text-emerald-700">{probe.availabilityPercent}%</strong></span>
                      <span>Avg Latency: <strong>{probe.avgLatencyMs}ms</strong></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 9. CHAOS & RESILIENCE TAB */}
        {activeTab === 'resilience-chaos' && (
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-bold text-slate-900">Controlled Staging Chaos & Circuit Breaker Experiments</h3>
                  <p className="text-xs text-slate-500">Hypothesis-driven fault injection validating automated fallback to Last-Known-Good states.</p>
                </div>
              </div>
              <div className="space-y-4">
                {qualityStore.chaosExperiments.map((exp) => (
                  <div key={exp.experimentId} className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-xs">
                    <div className="flex items-center justify-between">
                      <div className="font-bold text-slate-900">{exp.name}</div>
                      <span className="bg-emerald-100 text-emerald-800 px-2.5 py-0.5 rounded-full font-bold">{exp.status}</span>
                    </div>
                    <div className="mt-2 text-slate-600"><strong>Hypothesis:</strong> {exp.hypothesis}</div>
                    <div className="mt-2 text-slate-600"><strong>System Response:</strong> {exp.systemResponse}</div>
                    <div className="mt-2 text-indigo-800"><strong>Fallback Behavior:</strong> {exp.fallbackBehavior}</div>
                    <div className="mt-2 text-slate-500">Recovery Time: <strong className="text-emerald-700">{exp.recoveredInSeconds}s</strong></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 10. TEST DATA & PERSONAS TAB */}
        {activeTab === 'test-data' && (
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-bold text-slate-900">Test Data Platform & Zero-PII Personas</h3>
                  <p className="text-xs text-slate-500">Versioned statutory fixtures and fictitious employee datasets with strict PII scrubbers.</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {qualityStore.testDataPacks.map((pack) => (
                  <div key={pack.packId} className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-900">{pack.name}</span>
                      <span className="font-mono text-slate-500">{pack.version}</span>
                    </div>
                    <div className="mt-2 text-slate-600">Records: <strong>{pack.recordsCount.toLocaleString()}</strong></div>
                    <div className="mt-1 text-slate-500">Owner: {pack.owner}</div>
                    <div className="mt-3 flex items-center gap-2">
                      <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded">Zero PII</span>
                      <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded">Zero Live Secrets</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 11. DEFECTS & QUALITY DEBT TAB */}
        {activeTab === 'defects-debt' && (
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-bold text-slate-900">Quality Defect Tracker & Regression Generators</h3>
                  <p className="text-xs text-slate-500">Every resolved defect automatically generates an enduring regression test fixture.</p>
                </div>
                <button
                  onClick={() => setShowNewDefectModal(true)}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold flex items-center gap-1 transition-colors cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Log Defect</span>
                </button>
              </div>

              <div className="space-y-3">
                {qualityStore.defects.map((defect) => (
                  <div key={defect.defectId} className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-xs">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded font-bold text-[10px] ${
                          defect.severity === 'S0_CRITICAL' ? 'bg-red-100 text-red-800' :
                          defect.severity === 'S1_HIGH' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {defect.severity}
                        </span>
                        <span className="font-bold text-slate-900">{defect.title}</span>
                      </div>
                      <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-bold">{defect.status}</span>
                    </div>
                    <div className="mt-2 text-slate-600"><strong>Root Cause:</strong> {defect.rootCause}</div>
                    {defect.regressionTestCreated && (
                      <div className="mt-2 text-emerald-700 font-semibold flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span>Regression Test Linked: {defect.regressionTestId || 'Auto-generated'}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Quality Debt Register */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs">
              <h3 className="text-base font-bold text-slate-900 mb-2">Quality Debt Register</h3>
              <p className="text-xs text-slate-500 mb-4">Tracked engineering debt items with designated owners and target remediation versions.</p>
              <div className="space-y-3">
                {qualityStore.qualityDebt.map((debt) => (
                  <div key={debt.id} className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs">
                    <div>
                      <div className="font-bold text-slate-900">{debt.description}</div>
                      <div className="text-slate-500 mt-1">Owner: {debt.owner} • Age: {debt.ageDays} days</div>
                    </div>
                    <div className="text-slate-600">
                      Target: <strong className="text-slate-900">{debt.targetRemediationRelease}</strong>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 12. RELEASE CERTIFICATION TAB */}
        {activeTab === 'release-certification' && (
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-6 border-b border-slate-100">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Release Certification Gatekeeper</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Immutable audit certificates guaranteeing mathematical correctness, security clearance, and compliance gates prior to production ingress.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowNewExceptionModal(true)}
                    className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-colors cursor-pointer"
                  >
                    Grant Time-Bound Exception
                  </button>
                  <button
                    onClick={handleIssueCertificate}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
                  >
                    <Award className="w-4 h-4" />
                    <span>Issue Immutable Certificate</span>
                  </button>
                </div>
              </div>

              {/* Certificate Preview Card */}
              {qualityStore.releaseCertificates.length > 0 && (
                <div className="mt-6 p-6 rounded-2xl bg-gradient-to-br from-slate-900 to-blue-950 text-white border border-blue-800/40 shadow-sm">
                  <div className="flex items-center justify-between pb-4 border-b border-slate-800">
                    <div className="flex items-center gap-2.5">
                      <Award className="w-6 h-6 text-blue-400" />
                      <div>
                        <div className="text-sm font-bold tracking-tight">YAMOOPAY OFFICIAL RELEASE ASSURANCE CERTIFICATE</div>
                        <div className="text-[10px] text-slate-400 font-mono">{qualityStore.releaseCertificates[0].certificateId}</div>
                      </div>
                    </div>
                    <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-xs px-3 py-1 rounded-full font-bold">
                      {qualityStore.releaseCertificates[0].outcome}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-xs">
                    <div>
                      <div className="text-slate-400 text-[10px] uppercase">Version</div>
                      <div className="font-bold font-mono mt-0.5">{qualityStore.releaseCertificates[0].releaseVersion}</div>
                    </div>
                    <div>
                      <div className="text-slate-400 text-[10px] uppercase">Financial Verification</div>
                      <div className="font-bold text-emerald-400 mt-0.5">100% (44 Golden Cases)</div>
                    </div>
                    <div>
                      <div className="text-slate-400 text-[10px] uppercase">Properties Guaranteed</div>
                      <div className="font-bold text-indigo-300 mt-0.5">{qualityStore.releaseCertificates[0].propertiesVerified} Invariants</div>
                    </div>
                    <div>
                      <div className="text-slate-400 text-[10px] uppercase">Certified By</div>
                      <div className="font-bold mt-0.5">{qualityStore.releaseCertificates[0].certifiedBy}</div>
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-800/80 text-[11px] font-mono text-slate-400 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <div className="truncate">Artifact SHA-256: {qualityStore.releaseCertificates[0].artifactDigest}</div>
                    <div className="text-emerald-400 truncate">Signature: {qualityStore.releaseCertificates[0].immutableSignature}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal: Log New Defect */}
      {showNewDefectModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl border border-slate-200">
            <h3 className="text-base font-bold text-slate-900 mb-4">Log Quality Defect & Generate Regression</h3>
            <form onSubmit={handleCreateDefect} className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Defect Title</label>
                <input
                  type="text"
                  required
                  value={defectForm.title}
                  onChange={(e) => setDefectForm({ ...defectForm, title: e.target.value })}
                  placeholder="e.g. Rounding threshold on 2026 Ontario surtax"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Domain</label>
                  <select
                    value={defectForm.domain}
                    onChange={(e) => setDefectForm({ ...defectForm, domain: e.target.value as any })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  >
                    <option value="FINANCIAL_CORRECTNESS">FINANCIAL_CORRECTNESS</option>
                    <option value="ACCESSIBILITY">ACCESSIBILITY</option>
                    <option value="SECURITY">SECURITY</option>
                    <option value="PERFORMANCE">PERFORMANCE</option>
                    <option value="API">API</option>
                  </select>
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Severity</label>
                  <select
                    value={defectForm.severity}
                    onChange={(e) => setDefectForm({ ...defectForm, severity: e.target.value as any })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  >
                    <option value="S0_CRITICAL">S0_CRITICAL (Blocker)</option>
                    <option value="S1_HIGH">S1_HIGH</option>
                    <option value="S2_MEDIUM">S2_MEDIUM</option>
                    <option value="S3_LOW">S3_LOW</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block font-bold text-slate-700 mb-1">Root Cause Analysis</label>
                <textarea
                  rows={3}
                  required
                  value={defectForm.rootCause}
                  onChange={(e) => setDefectForm({ ...defectForm, rootCause: e.target.value })}
                  placeholder="Describe mathematical, logic, or environmental cause..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowNewDefectModal(false)}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg font-bold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-bold"
                >
                  Create & Link Regression Test
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Grant Quality Exception */}
      {showNewExceptionModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl border border-slate-200">
            <h3 className="text-base font-bold text-slate-900 mb-4">Grant Time-Bound Quality Exception</h3>
            <form onSubmit={handleCreateException} className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Quality Gate Domain</label>
                <select
                  value={exceptionForm.gateDomain}
                  onChange={(e) => setExceptionForm({ ...exceptionForm, gateDomain: e.target.value as any })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                >
                  <option value="PERFORMANCE">PERFORMANCE (e.g. latency warning)</option>
                  <option value="ACCESSIBILITY">ACCESSIBILITY (non-critical minor)</option>
                  <option value="SEO">SEO (minor canonical header)</option>
                  <option value="MOBILE">MOBILE (specific device edge case)</option>
                </select>
              </div>
              <div>
                <label className="block font-bold text-slate-700 mb-1">Risk Description</label>
                <input
                  type="text"
                  required
                  value={exceptionForm.riskDescription}
                  onChange={(e) => setExceptionForm({ ...exceptionForm, riskDescription: e.target.value })}
                  placeholder="e.g. P95 latency increased by 12ms during peak stress simulation"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block font-bold text-slate-700 mb-1">Justification & Mitigation</label>
                <textarea
                  rows={3}
                  required
                  value={exceptionForm.justification}
                  onChange={(e) => setExceptionForm({ ...exceptionForm, justification: e.target.value })}
                  placeholder="Explain why release can proceed safely and mitigation in place..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowNewExceptionModal(false)}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg font-bold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-bold"
                >
                  Approve Time-Bound Waiver
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
